import React from "react";
import moment from "moment-timezone";
import { RSHIELD_WHEN_LIMIT_REACHED } from "../../../../StateManagement/actions";

/**
 * rShieldConfig: the ONE version being viewed (not a list of versions):
 * {
 *   id, updatedOn, rShieldEnabled,
 *   options: { resumeOnFullScreen, focusLossLimit, focusLossLimitValue, notifySender, banReader },
 *   changed: { resumeOnFullScreen, focusLossLimit, notifySender, banReader }
 * }
 *
 * `changed` is computed by the caller (DocumentHistoryPopup) by diffing this
 * version's options against the immediately-preceding version, field by
 * field. Each flag here highlights ONLY that single line in yellow - nothing
 * else in the block is affected, and no other versions are shown.
 */
const RShieldConfigurationsModal = ({
  isOpen,
  onClose,
  selectedLanguage,
  rShieldConfig,
  dateFormatValueUpper,
  applyTimeZoneToDisplay,
  timeZoneRef,
}) => {
  if (!isOpen) return null;

  const config = rShieldConfig || {};
  const opts = config.options || {};
  const changed = config.changed || {};

  const yesNo = (value) =>
    value
      ? selectedLanguage.LstLanguageCodes.UI_Enabled || "Enabled"
      : selectedLanguage.LstLanguageCodes.UI_Disabled || "Disabled";

  const whenLimitReachedLabel = (value) =>
    value === RSHIELD_WHEN_LIMIT_REACHED.REAUTHENTICATE
      ? selectedLanguage.LstLanguageCodes.UI_Reauthenticate || "Reauthenticate"
      : selectedLanguage.LstLanguageCodes.UI_EndSession || "End Session";

  const formatDate = (rawDate) =>
    rawDate && dateFormatValueUpper && applyTimeZoneToDisplay && timeZoneRef
      ? moment
        .utc(applyTimeZoneToDisplay(new Date(rawDate), timeZoneRef.current))
        .format(dateFormatValueUpper + " HH:mm")
      : rawDate;
  const ConfigRow = ({ label, value, isChanged }) => (
    <div
      className={"d-flex justify-content-between rshieldConfigRow" + (isChanged ? " bgYellow" : "")}
      style={{
        paddingLeft: "20px",
        paddingRight: "10px",
        paddingTop: "6px",
        paddingBottom: "6px",
        backgroundColor: isChanged ? "#ffff00" : "transparent",
      }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
  const SectionHeader = ({ label, value, isChanged }) => (
    <div
      className={"d-flex justify-content-between rshieldConfigHeader" + (isChanged ? " bgYellow" : "")}
      style={{
        paddingRight: "10px",
        paddingBottom: "8px",
        backgroundColor: isChanged ? "#ffff00" : "transparent",
      }}>
      <span className="fw-bold">{label}</span>
      {value !== undefined ? <span>{value}</span> : null}
    </div>
  );
  const SectionBlock = ({ children }) => (
    <div
      className="rshieldConfigBlock"
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px 16px",
        marginBottom: "16px",
        backgroundColor: "#fafafa",
      }}>
      {children}
    </div>
  );

  const isFocusProtectionOn = !!opts.focusProtection;
  const isFocusProtectionChanged = changed.resumeOnFullScreen === true || changed.focusLossLimit === true || changed.focusProtection === true || changed.whenLimitReached === true;

  return (
    <div className="modalCus rshieldConfig" id="rShieldConfigurationsModal">
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: "480px", width: "95%" }}>
        <div className="modal-content">
          <div className="modal-body">
            {/* Modal Header */}
            <div
              className="d-flex justify-content-between align-items-center pb-2"
              style={{ borderBottom: "2px solid #918d8d4d" }}>
              <h4 className="modal-heading mb-0" style={{ margin: "0.5%" }}>
                {selectedLanguage.LstLanguageCodes.UI_RShieldConfigurations || "RShield Configurations"}
              </h4>
              <span
                className="material-icons pointer"
                style={{ color: "#dc3545" }}
                onClick={onClose}>
                cancel
              </span>
            </div>
            <div className="mt-3">
              <SectionBlock>
                <SectionHeader
                  label={selectedLanguage.LstLanguageCodes.UI_FocusProtection || "Focus Protection"}
                  value={yesNo(isFocusProtectionOn)}
                  isChanged={isFocusProtectionChanged}
                />
                <ConfigRow
                  label={selectedLanguage.LstLanguageCodes.UI_ViewOnlyinFullScreen || "View Only in Full Screen"}
                  value={yesNo(opts.resumeOnFullScreen)}
                  isChanged={changed.resumeOnFullScreen === true}
                />
                <ConfigRow
                  label={selectedLanguage.LstLanguageCodes.UI_FocusLossLimit || "Focus Loss Limit"}
                  value={
                    yesNo(opts.focusLossLimit) +
                    (opts.focusLossLimit ? " " + opts.focusLossLimitValue : "")
                  }
                  isChanged={changed.focusLossLimit === true}
                />
                {<ConfigRow
                  label={selectedLanguage.LstLanguageCodes.UI_WhenLimitisReached || "When Limit is Reached"}
                  value={opts.focusLossLimit ? whenLimitReachedLabel(opts.whenLimitReached) : (selectedLanguage.LstLanguageCodes.UI_NotApplicable || "N/A")}
                  isChanged={changed.whenLimitReached === true}
                />}
              </SectionBlock>

              <SectionBlock>
                <SectionHeader
                  label={selectedLanguage.LstLanguageCodes.UI_CaptureDetection || "Capture Detection"}
                />
                <ConfigRow
                  label={selectedLanguage.LstLanguageCodes.UI_NotifySender || "Notify Sender"}
                  value={yesNo(opts.notifySender)}
                  isChanged={changed.notifySender === true}
                />
                <ConfigRow
                  label={selectedLanguage.LstLanguageCodes.UI_BanReader || "Ban Reader"}
                  value={yesNo(opts.banReader)}
                  isChanged={changed.banReader === true}
                />
              </SectionBlock>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RShieldConfigurationsModal;